import Checklist from "@editorjs/checklist";
import CodeTool from "@editorjs/code";
import Delimiter from "@editorjs/delimiter";
import EditorJS, { type OutputData } from "@editorjs/editorjs";
import Header from "@editorjs/header";
import List from "@editorjs/list";
import Marker from "@editorjs/marker";
import Underline from "@editorjs/underline";
import { useEffect, useRef } from "react";

import { cn } from "@/lib/utils";

interface TextEditorProps {
  initialData?: OutputData;
  canEdit?: boolean;
  onUpdateNote?: (data: OutputData) => void;
  className?: string;
}

/** Thin EditorJS wrapper — port of components/ui/TextEditor.vue. */
export function TextEditor({ initialData, canEdit = true, onUpdateNote, className }: TextEditorProps) {
  const holderRef = useRef<HTMLDivElement>(null);
  const editorRef = useRef<EditorJS | null>(null);
  const onUpdateNoteRef = useRef(onUpdateNote);
  onUpdateNoteRef.current = onUpdateNote;
  // Skip the re-render effect's first run: the constructor's own `data:` already handles
  // the initial mount (mirrors Vue's non-immediate `watch`, which never fires on mount either).
  const isFirstRender = useRef(true);

  // biome-ignore lint/correctness/useExhaustiveDependencies: EditorJS is created once per mount, matching the Vue onMounted (readOnly isn't reactive there either)
  useEffect(() => {
    if (!holderRef.current) {
      return;
    }
    const editor = new EditorJS({
      holder: holderRef.current,
      readOnly: canEdit === false,
      tools: {
        checklist: { class: Checklist, inlineToolbar: true },
        underline: Underline,
        marker: Marker,
        header: Header,
        delimiter: Delimiter,
        list: List,
        code: CodeTool,
      },
      data: initialData ?? { blocks: [] },
      onChange: async (api) => {
        const data = await api.saver.save();
        onUpdateNoteRef.current?.(data);
      },
    });
    editorRef.current = editor;

    return () => {
      editor.isReady
        .then(() => editor.destroy())
        .catch(() => {
          // Editor never finished initializing — nothing to tear down.
        });
      editorRef.current = null;
    };
  }, []);

  // Re-render when the claim's notes change (mirrors the Vue watcher — this
  // editor is reused across claims, so this drives most loads, not the
  // initial `data:`). Await `isReady` since EditorJS initializes async.
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    const editor = editorRef.current;
    if (!editor) {
      return;
    }
    editor.isReady
      .then(() => editor.render(initialData ?? { blocks: [] }))
      .catch(() => {
        // Editor was destroyed before this render landed — ignore.
      });
  }, [initialData]);

  return (
    <div
      ref={holderRef}
      className={cn(
        "codex-editor-holder block w-full rounded-md border border-gray-300 bg-white p-3 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm",
        className,
      )}
    />
  );
}
